// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { NumericFormat } from 'react-number-format';
import { selectWorkflowType } from '@advisor-app/store/slices/workflow';
import { Slider } from './slider/slider';
import { useAppSelector } from '@advisor-app/store/store';
import { useMemo } from 'react';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import { useTranslation } from 'react-i18next';

interface SliderCashProps {
  value: number;
  onInputBlur?: () => void;
  onChangeCommitted?: () => void;
  onChangeInputValue: (value: number) => void;
  onChangeSlider: (value: number) => void;
  min?: number;
}

export const SliderCash = ({
  onChangeCommitted,
  onChangeInputValue,
  onChangeSlider,
  onInputBlur,
  value,
  min = 0,
}: SliderCashProps): JSX.Element => {
  const { t } = useTranslation();
  const workflow = useAppSelector(selectWorkflowType);
  const currency = useAppSelector(selectAccountCurrency);

  const prefix = useMemo(() => {
    const currencySymbols: { [key in AccountCurrency]: string } = {
      USD: '$',
      EUR: '€',
    };
    return currencySymbols[currency];
  }, [currency]);

  const specificValue = Math.trunc(value);

  let minVal = 5000;
  if (workflow !== 'new') {
    minVal = 0;
  }

  minVal = Math.max(minVal, min);

  const inputMaxVal = 100_000_000;
  const sliderMaxVal = 10_000_000;

  return (
    <>
      <NumericFormat
        value={specificValue}
        thousandSeparator
        min={minVal}
        maxLength={11}
        isAllowed={
          /* istanbul ignore next */ ({ value }) => {
            return Number(value) <= inputMaxVal;
          }
        }
        onValueChange={
          /* istanbul ignore next */ ({ value }) =>
            onChangeInputValue(Number(value))
        }
        onBlur={onInputBlur}
        prefix={prefix}
        placeholder={t('common.investment', { ns: 'common' }) as string}
        className="input-common !money-display mt-3"
        id="investment-input"
      />
      <Slider
        valueLabelDisplay="off"
        onChange={(_, newValue) => {
          onChangeSlider(newValue as number);
        }}
        onChangeCommitted={onChangeCommitted}
        value={value}
        min={minVal}
        max={sliderMaxVal}
        step={1000}
        valueLabelFormat={val => formatToMoney(val)}
        data-testid="slider"
        className="mt-4 !block"
      />
    </>
  );
};
